import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { classNames } from '../EddFilesTable/EddFilesTable';
import { capitalize } from '../../../containers/Pages/Edd/EddUtils';

export default function ErrorTooltip({ error }) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  const heading = error.action
    ? `${capitalize(error.action)} ${capitalize(error.fileType)} Error`
    : 'File Error';

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleClickOutside = e => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  return (
    <div
      ref={containerRef}
      className="absolute w-full bg-transparent h-10 overflow-visible z-[100000000]"
    >
      <button
        type="button"
        aria-expanded={isOpen}
        aria-label={heading}
        className="absolute inset-0 w-full h-full cursor-pointer bg-transparent border-none p-0"
        onClick={() => setIsOpen(prev => !prev)}
      />
      <div
        className={classNames(
          'absolute top-[17px] w-72 duration-300',
          isOpen ? 'opacity-100 visible -translate-y-[210px]' : 'opacity-0 invisible',
        )}
      >
        <div className="bg-white w-full">
          <div className="h-12 w-[100%] flex items-center justify-between bg-slate-400/10 backdrop-blur-[24px] outline-solid outline-white transition-all">
            <div
              className={classNames(
                'relative left-[10px] flex justify-start delay-300 duration-300 transition-all ease-in',
                isOpen ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-1',
              )}
            >
              <i className="material-icons text-[24px] !text-red-600">error_outline</i>
            </div>
            <div>
              <p
                className={classNames(
                  'font-bold text-dark-night text-sm p-2 delay-300 duration-300 transition-all ease-in',
                  isOpen ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-1',
                )}
              >
                {heading}
              </p>
            </div>
            <div />
          </div>
        </div>
        <div className="w-full min-h-16 bg-white border flex flex-col items-center justify-center">
          {error.rowErrors.map(rowError => (
            <p
              key={rowError.id}
              className={classNames(
                'delay-300 duration-300 transition-all text-center ease-in p-4 pt-2',
                isOpen ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-1',
              )}
            >
              {capitalize(rowError.value)}
            </p>
          ))}
        </div>
      </div>
    </div>
  );
}

ErrorTooltip.propTypes = {
  error: PropTypes.shape({
    action: PropTypes.string,
    fileType: PropTypes.string,
    rowErrors: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired,
      }),
    ).isRequired,
  }).isRequired,
};
